import { ClientInputs, ClientOutputs, type File as BotpressFile } from '@botpress/client'
import { LucideIcon } from 'lucide-react'
import { queryFunctions } from '~/services'

//#region Knowledge Base Query Types

export type CreateKnowledgebaseParams = { workspaceId: string; botId: string } & ClientInputs['createKnowledgeBase']

export type ListKnowledgeBasesParams = { workspaceId: string; botId: string } & ClientInputs['listKnowledgeBases']

export type GetKnowledgeBasesParams = {
  workspaceId: string
  botId: string
  kbId: string
} & ClientInputs['listKnowledgeBases']

export type UpdateKnowledgeBaseParams = {
  workspaceId: string
  botId: string
  onSuccess?: () => void
} & ClientInputs['updateKnowledgeBase']

export type DeleteKnowledgeBaseParams = {
  workspaceId: string
  botId: string
  onSuccess?: () => void
} & ClientInputs['deleteKnowledgeBase']

export type ListKnowledgeBasesResponse = {
  pages: Awaited<ReturnType<(typeof queryFunctions)['workspaces_/$workspaceId_/bots_/$botId_/knowledgeBases_']>>[]
  pageParams: string | undefined
}

//#endregion

//#region Knowledge Source Query Types

export type ListKnowledgeSourceResponse = {
  pages: Awaited<
    ReturnType<
      (typeof queryFunctions)['workspaces_/$workspaceId_/bots_/$botId_/knowledgeBases_/$kbId_/sources/$tags/all']
    >
  >[]
  pageParams: string | undefined
}

export type ListKnowledgeSourceParams = {
  workspaceId: string
  botId: string
  kbId: string
  dsType?: KnowledgeSourceType
} & ClientInputs['listFiles']

export type GetKnowledgeSourceFileParams = {
  workspaceId: string
  botId: string
} & ClientInputs['getFile']

export type ListWebPageSourceParams = {
  workspaceId: string
  botId: string
  kbId: string
  dsId: string
} & ClientInputs['listFiles']

export type DeleteKnowledgeSourceParams = {
  workspaceId: string
  botId: string
  kbId: string
  onSuccess?: () => void
} & ClientInputs['deleteFile']

export type DeleteKnowledgeSourcesParams = {
  workspaceId: string
  botId: string
  kbId: string
  ids?: string[]
  dsIds?: string[]
  onSuccess?: () => void
}

export type DeleteWebPageSourceParams = {
  workspaceId: string
  botId: string
  kbId: string
  dsId: string
  onSuccess?: () => void
}

export type UploadDocumentSourcesParams = {
  workspaceId: string
  botId: string
  kbId: string
  files: File[]
  onSuccess?: () => void
}

export type UploadRichTextSourcesParams = {
  workspaceId: string
  botId: string
  kbId: string
  title: string
  content: string
  onSuccess?: () => void
}

export type UploadWebPageSourceParams = {
  workspaceId: string
  botId: string
  kbId: string
  dsId: string
  pageUrl: string
  urls: string[]
  onSuccess?: () => void
}

export type RenameKnowledgeSourceParams = {
  workspaceId: string
  botId: string
  kbId: string
  name: string
  onSuccess?: () => void
} & ClientInputs['updateFileMetadata']

export type RenameWebPageSourceParams = {
  workspaceId: string
  botId: string
  kbId: string
  dsId: string
  name: string
  onSuccess?: () => void
}

export type UpdateCrawlScheduleParams = {
  workspaceId: string
  botId: string
  kbId: string
  dsId: string
  frequency: CrawlFrequency
  onSuccess?: () => void
}

//#endregion

//#region Knowledge Base Types

export type KnowledgeBaseSummary = {
  oldKbId?: string
} & ClientOutputs['listKnowledgeBases']['knowledgeBases'][number]

export type KnowledgeBase = {
  knowledgeSources: ClientOutputs['listFiles']
} & KnowledgeBaseSummary
//#endregion

//#region Knowledge Source Types
export const KnowledgeSourceTypes = [
  'document',
  'rich-text',
  'web-page',
  'google-drive',
  'notion',
  'confluence',
] as const
export type KnowledgeSourceType = (typeof KnowledgeSourceTypes)[number]
export type KnowledgeSource = ClientOutputs['listFiles']['files'][number]
export type KnowledgeSourceStatusType = BotpressFile['status']

//#endregion

export const CrawlFrequencies = ['daily', 'weekly', 'monthly', 'never'] as const
export type CrawlFrequency = (typeof CrawlFrequencies)[number]

export type RecurringSync = Record<Exclude<CrawlFrequency, 'never'>, string[]>

export type RecurringEventPayload = ClientInputs['updateBot']['recurringEvents']

export type DSData = {
  createdOn: number
  title: string
  type: string
  data: {
    websiteUrl: string
    pages: string[]
    indexingJobs: string[]
    crawlFrequency?: CrawlFrequency
    cronSchedule?: string
  }
}

export type KnowledgeBaseDropdownItem = {
  label: string
  icon?: LucideIcon
  destructive?: boolean
  onClick: () => void
}

export type KnowledgeSourceTableRow = {
  index: number
}

export type KnowledgeSourceStepProps = {
  close: () => void
}

export type AddKnowledgeSourceStep = 'select_content' | 'upload_documents' | 'upload_rich_text' | 'sync_website'

export type AddKnowledgeSourceContentProps = {
  workspaceId: string
  botId: string
  kbId: string
  dsId?: string
  startStep?: AddKnowledgeSourceStep
  hideBackArrow?: boolean
  close: () => void
}

export type DiscoverUrlsParams = {
  workspaceId: string
  botId: string
  url: string
  includeGlobs: string[]
  excludeGlobs: string[]
  onSuccess?: (urls: string[]) => void
  onError?: () => void
}
